import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopoverClose } from '@radix-ui/react-popover';
import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { toast } from 'sonner';
import {
  AlertCircle,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Circle,
  FileSpreadsheet,
  FolderKanban,
  FolderTree,
  GitBranch,
  Lightbulb,
  LucideIcon,
  Network,
  Rocket,
  Users,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { EVENTO_DADOS_ALTERADOS } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { PassoChave, PrimeirosPassosStatus, primeirosPassosService } from '@/services/primeirosPassosApi';

/**
 * Tour de primeiros passos do admin (docs/tour-primeiros-passos-2026-10-17.md).
 * Boas-vindas no primeiro acesso + checklist no cabeçalho; cada item leva à tela certa e destaca (driver.js)
 * o botão de criação, marcado na página com `data-tour="<alvo>"` (e, quando há, um atalho em seguida).
 * Um passo conta como feito quando a empresa já tem pelo menos um registro daquele tipo; ao concluir um,
 * um aviso oferece seguir para o próximo.
 */

interface Balao {
  alvo: string;
  titulo: string;
  texto: string;
}

interface Passo {
  chave: PassoChave;
  titulo: string;
  descricao: string;
  url: string;
  icone: LucideIcon;
  /** Módulo que precisa estar habilitado na empresa pro passo aparecer */
  modulo?: string;
  /** Destaque principal (botão que conclui o passo) */
  destaque: Balao;
  /** Segundo balão opcional: atalho que o Kora já oferece pra esse passo */
  atalho?: Balao;
  /** Dica curta exibida no checklist */
  dica?: string;
  /** Passos que é melhor fazer antes (aviso no checklist, não bloqueia) */
  dependeDe?: PassoChave[];
}

const PASSOS: Passo[] = [
  {
    chave: 'departamentos',
    titulo: 'Cadastrar departamentos',
    descricao: 'As áreas da empresa (Comercial, RH, Financeiro...).',
    url: '/departamentos',
    icone: FolderTree,
    destaque: {
      alvo: 'novo-departamento',
      titulo: 'Crie seu primeiro departamento',
      texto: 'Comece pelas áreas da empresa. Os departamentos organizam cargos, pessoas e o organograma.',
    },
  },
  {
    chave: 'cargos',
    titulo: 'Cadastrar cargos',
    descricao: 'As funções de cada área e quantas pessoas cada uma comporta.',
    url: '/cargos',
    icone: Briefcase,
    dica: 'Tem muitos cargos? Importe de uma planilha.',
    dependeDe: ['departamentos'],
    destaque: {
      alvo: 'novo-cargo',
      titulo: 'Agora, os cargos',
      texto: 'Cadastre as funções da empresa e vincule cada uma ao seu departamento.',
    },
    atalho: {
      alvo: 'importar-cargos',
      titulo: 'Tem muitos cargos?',
      texto: 'Baixe o modelo, preencha no Excel e importe todos de uma vez, já com departamento e quantidade.',
    },
  },
  {
    chave: 'usuarios',
    titulo: 'Cadastrar a equipe',
    descricao: 'Dê acesso às pessoas com departamento, cargo e gestor de cada uma.',
    url: '/usuarios',
    icone: Users,
    dica: 'Informe departamento, cargo e gestor: é isso que monta o organograma sozinho.',
    dependeDe: ['cargos'],
    destaque: {
      alvo: 'novo-usuario',
      titulo: 'Traga sua equipe',
      texto: 'Cadastre as pessoas com departamento, cargo e gestor. Assim o organograma se monta sozinho.',
    },
  },
  {
    chave: 'projetos',
    titulo: 'Criar um projeto',
    descricao: 'Organize entregas com fases, seções e atividades.',
    url: '/projetos',
    icone: FolderKanban,
    modulo: 'projetos',
    dica: 'Comece por um projeto real em andamento. Depois, use "Gerar KR" numa atividade para levá-la aos OKRs: a seção vira o OKR e a atividade, um Key Result.',
    destaque: {
      alvo: 'novo-projeto',
      titulo: 'Seu primeiro projeto',
      texto: 'Crie um projeto para acompanhar entregas, responsáveis e prazos.',
    },
  },
  {
    chave: 'organogramas',
    titulo: 'Montar o organograma',
    descricao: 'O Kora monta a estrutura da empresa a partir dos cadastros.',
    url: '/organograma',
    icone: Network,
    modulo: 'organograma',
    dependeDe: ['cargos', 'usuarios'],
    destaque: {
      alvo: 'gerar-organograma',
      titulo: 'Gere o organograma em um clique',
      texto: 'O Kora monta o organograma sozinho com os departamentos, cargos e gestores que você cadastrou.',
    },
    atalho: {
      alvo: 'novo-organograma',
      titulo: 'Prefere desenhar do zero?',
      texto: 'Também dá para criar um organograma em branco e montar cada caixa à mão.',
    },
  },
  {
    chave: 'fluxogramas',
    titulo: 'Mapear um processo',
    descricao: 'Desenhe um fluxograma com formas ANSI/BPMN.',
    url: '/fluxogramas',
    icone: GitBranch,
    modulo: 'fluxogramas',
    dica: 'Comece por um template pronto e adapte ao seu processo.',
    destaque: {
      alvo: 'novo-fluxograma',
      titulo: 'Mapeie um processo',
      texto: 'Crie um fluxograma (do zero ou a partir de um template) para documentar como o trabalho acontece.',
    },
  },
];

/** O que o destaque (driver.js) precisa: a tela e os balões. Vale para um passo ou para o atalho da planilha. */
type Destaque = Pick<Passo, 'url' | 'destaque' | 'atalho'>;

/**
 * Atalho para quem já tem a equipe numa planilha (cadastro inteligente, docs/cadastro-inteligente-2026-11-01.md):
 * cria departamentos, cargos e pessoas de uma vez, concluindo os 3 primeiros passos.
 */
const CADASTRO_INTELIGENTE: Destaque = {
  url: '/cadastro-inteligente',
  destaque: {
    alvo: 'cadastro-inteligente',
    titulo: 'Envie a planilha da equipe',
    texto: 'Baixe o modelo, preencha uma linha por pessoa e envie. Antes de gravar, o Kora mostra uma prévia.',
  },
};
const PASSOS_DA_PLANILHA: PassoChave[] = ['departamentos', 'cargos', 'usuarios'];

const chaveAutoAbertura = (usuarioId: string) => `kora:primeiros-passos:auto-aberto:${usuarioId}`;
const chaveBoasVindas = (usuarioId: string) => `kora:primeiros-passos:boas-vindas:${usuarioId}`;

const lerStorage = (storage: () => Storage, chave: string) => {
  try {
    return storage().getItem(chave);
  } catch {
    return 'indisponivel'; // sem storage: não insiste em mostrar nada automaticamente
  }
};
const gravarStorage = (storage: () => Storage, chave: string) => {
  try {
    storage().setItem(chave, '1');
  } catch {
    // ignora: é só conveniência
  }
};

// Cada rota monta o próprio AppLayout, então o provider é recriado a cada navegação: o status e o
// destaque pendente ficam em memória fora do componente pra sobreviver à troca de tela.
let statusEmCache: PrimeirosPassosStatus | null = null;
let empresaDoStatus: string | null = null;
let alvoEmCache: Destaque | null = null;
let cacheDoUsuarioId: string | null = null;
// Só a resposta da requisição mais recente vale (com o backend lento, uma resposta antiga chegando depois
// regravaria um progresso desatualizado e repetiria o aviso de "feito!").
let ultimaRequisicao = 0;
// Ações do provider montado AGORA: o botão do aviso (toast) sobrevive à troca de tela, mas o provider que o
// criou é desmontado nela — chamar o dele usaria rota e estado velhos.
let acoesAtivas: { irPara: (passo: Passo) => void; concluir: () => Promise<void> } | null = null;

type PassoComEstado = Passo & { feito: boolean; faltaAntes: Passo[] };

interface PrimeirosPassosContexto {
  ativo: boolean;
  podeReabrir: boolean;
  passos: PassoComEstado[];
  feitos: number;
  aberto: boolean;
  setAberto: (aberto: boolean) => void;
  irPara: (passo: Passo) => void;
  /** Leva ao cadastro inteligente; `null` quando os passos que ele cobre já estão feitos. */
  irParaPlanilha: (() => void) | null;
  concluir: () => Promise<void>;
  reabrir: () => Promise<void>;
}

const Contexto = createContext<PrimeirosPassosContexto | null>(null);

export function usePrimeirosPassos() {
  return useContext(Contexto);
}

export function PrimeirosPassosProvider({ children }: { children: ReactNode }) {
  const { user, empresaAtiva } = useAuth();
  const { isModuleEnabled } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  if ((user?.id ?? null) !== cacheDoUsuarioId) {
    statusEmCache = null;
    empresaDoStatus = null;
    alvoEmCache = null;
    cacheDoUsuarioId = user?.id ?? null;
  }
  const [status, setStatusLocal] = useState<PrimeirosPassosStatus | null>(statusEmCache);
  const [aberto, setAbertoLocal] = useState(false);
  const [boasVindasAberta, setBoasVindasAberta] = useState(false);
  const [alvoPendente, setAlvoLocal] = useState<Destaque | null>(alvoEmCache);

  const ehAdmin = user?.tipo === 'admin';
  const empresaId = empresaAtiva?.id ?? null;

  const passosVisiveis = useMemo(() => PASSOS.filter((p) => !p.modulo || isModuleEnabled(p.modulo)), [isModuleEnabled]);

  const setStatus = useCallback((valor: PrimeirosPassosStatus | null) => {
    statusEmCache = valor;
    setStatusLocal(valor);
  }, []);
  const setAlvoPendente = useCallback((alvo: Destaque | null) => {
    alvoEmCache = alvo;
    setAlvoLocal(alvo);
  }, []);

  const irPara = useCallback(
    (passo: Destaque) => {
      setAbertoLocal(false);
      setBoasVindasAberta(false);
      setAlvoPendente(passo);
      if (location.pathname !== passo.url) navigate(passo.url);
    },
    [location.pathname, navigate, setAlvoPendente],
  );

  const concluir = useCallback(async () => {
    const resposta = await primeirosPassosService.concluir();
    if (!resposta.success) {
      toast.error(resposta.error || 'Não foi possível concluir o tour');
      return;
    }
    setAbertoLocal(false);
    if (statusEmCache) {
      setStatus({ ...statusEmCache, concluido_em: resposta.data?.concluido_em ?? new Date().toISOString() });
    }
    toast.success('Tour encerrado. Para ver de novo, use "Primeiros passos" no menu do seu perfil.');
  }, [setStatus]);

  // Ref pra usar a lista atual dentro do carregar sem recriá-lo a cada render
  const passosRef = useRef(passosVisiveis);
  passosRef.current = passosVisiveis;

  useEffect(() => {
    const acoes = { irPara, concluir };
    acoesAtivas = acoes;
    return () => {
      if (acoesAtivas === acoes) acoesAtivas = null;
    };
  }, [irPara, concluir]);

  /** Avisa os passos que acabaram de ser concluídos e oferece seguir para o próximo. */
  const celebrar = useCallback((anterior: PrimeirosPassosStatus, novo: PrimeirosPassosStatus) => {
    if (anterior.concluido_em || novo.concluido_em) return;
    const passos = passosRef.current;
    const recemFeitos = passos.filter((p) => anterior.contagens[p.chave] === 0 && novo.contagens[p.chave] > 0);
    if (recemFeitos.length === 0) return;
    const proximo = passos.find((p) => novo.contagens[p.chave] === 0);
    const feitos = passos.filter((p) => novo.contagens[p.chave] > 0).length;
    const titulo = recemFeitos.length === 1 ? `${recemFeitos[0].titulo}: feito!` : `${recemFeitos.length} passos concluídos!`;
    if (proximo) {
      toast.success(titulo, {
        description: `${feitos} de ${passos.length} prontos. Próximo: ${proximo.titulo.toLowerCase()}.`,
        duration: 12000,
        action: { label: 'Continuar', onClick: () => acoesAtivas?.irPara(proximo) },
      });
    } else {
      toast.success('Tudo pronto! Sua empresa está configurada no Kora.', {
        description: 'Você concluiu todos os primeiros passos.',
        duration: 12000,
        action: { label: 'Concluir tour', onClick: () => acoesAtivas?.concluir() },
      });
    }
  }, []);

  const carregar = useCallback(async () => {
    if (!ehAdmin) return;
    const requisicao = ++ultimaRequisicao;
    const resposta = await primeirosPassosService.getStatus();
    if (requisicao !== ultimaRequisicao || !resposta.success || !resposta.data) return;
    const anterior = empresaDoStatus === empresaId ? statusEmCache : null;
    empresaDoStatus = empresaId;
    setStatus(resposta.data);
    if (anterior) celebrar(anterior, resposta.data);
  }, [ehAdmin, empresaId, setStatus, celebrar]);

  // Recarrega ao trocar de tela ou de empresa: o progresso vem dos cadastros
  useEffect(() => {
    if (!ehAdmin) {
      setStatus(null);
      return;
    }
    // Tour concluído/pulado: nada a acompanhar até o admin reabrir (o reabrir recarrega)
    if (statusEmCache?.concluido_em && empresaDoStatus === empresaId) return;
    carregar();
  }, [ehAdmin, empresaId, carregar, setStatus, location.pathname]);

  const ativo = ehAdmin && !!status && !status.concluido_em;

  // Qualquer gravação na API (criar departamento, cargo, usuário...) recalcula o progresso
  useEffect(() => {
    if (!ativo) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const aoAlterar = (evento: Event) => {
      const endpoint = (evento as CustomEvent<{ endpoint?: string }>).detail?.endpoint ?? '';
      if (endpoint.startsWith('/primeiros-passos') || endpoint.startsWith('/notificacoes')) return;
      clearTimeout(timer);
      timer = setTimeout(carregar, 600);
    };
    window.addEventListener(EVENTO_DADOS_ALTERADOS, aoAlterar);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(EVENTO_DADOS_ALTERADOS, aoAlterar);
    };
  }, [ativo, carregar]);

  const setAberto = useCallback(
    (abrir: boolean) => {
      setAbertoLocal(abrir);
      if (abrir) carregar();
    },
    [carregar],
  );

  const passos = useMemo<PassoComEstado[]>(() => {
    const feito = (chave: PassoChave) => (status?.contagens[chave] ?? 0) > 0;
    return passosVisiveis.map((p) => ({
      ...p,
      feito: feito(p.chave),
      faltaAntes: feito(p.chave)
        ? []
        : (p.dependeDe ?? []).map((c) => passosVisiveis.find((q) => q.chave === c)).filter((q): q is Passo => !!q && !feito(q.chave)),
    }));
  }, [status, passosVisiveis]);
  const feitos = passos.filter((p) => p.feito).length;
  const tudoFeito = passos.length > 0 && feitos === passos.length;
  const planilhaUtil = passos.some((p) => PASSOS_DA_PLANILHA.includes(p.chave) && !p.feito);
  const irParaPlanilha = useCallback(() => irPara(CADASTRO_INTELIGENTE), [irPara]);

  // Primeiro acesso: boas-vindas (uma vez por navegador). Depois: checklist abre sozinho uma vez por sessão.
  useEffect(() => {
    if (!ativo || !user) return;
    if (!tudoFeito && !lerStorage(() => localStorage, chaveBoasVindas(user.id))) {
      gravarStorage(() => localStorage, chaveBoasVindas(user.id));
      gravarStorage(() => sessionStorage, chaveAutoAbertura(user.id));
      setBoasVindasAberta(true);
      return;
    }
    if (lerStorage(() => sessionStorage, chaveAutoAbertura(user.id))) return;
    gravarStorage(() => sessionStorage, chaveAutoAbertura(user.id));
    setAbertoLocal(true);
  }, [ativo, user, tudoFeito]);

  // Destaca o botão do passo (e o atalho, se houver) quando a tela termina de carregar
  useEffect(() => {
    if (!alvoPendente || location.pathname !== alvoPendente.url) return;
    let cancelado = false;
    let tentativas = 0;
    const procurar = () => {
      if (cancelado) return;
      const elemento = document.querySelector<HTMLElement>(`[data-tour="${alvoPendente.destaque.alvo}"]`);
      if (!elemento) {
        // Até ~30 s: com o backend lento algumas telas ficam vários segundos só no loader
        if (++tentativas < 150) setTimeout(procurar, 200);
        else setAlvoPendente(null);
        return;
      }
      const atalho = alvoPendente.atalho
        ? document.querySelector<HTMLElement>(`[data-tour="${alvoPendente.atalho.alvo}"]`)
        : null;
      const passos: DriveStep[] = [
        { element: elemento, popover: { title: alvoPendente.destaque.titulo, description: alvoPendente.destaque.texto, side: 'bottom', align: 'end' } },
      ];
      if (atalho && alvoPendente.atalho) {
        passos.push({ element: atalho, popover: { title: alvoPendente.atalho.titulo, description: alvoPendente.atalho.texto, side: 'bottom', align: 'end' } });
      }
      const tour = driver({
        popoverClass: 'kora-tour',
        stagePadding: 6,
        stageRadius: 8,
        showButtons: passos.length > 1 ? ['next', 'previous'] : ['next'],
        nextBtnText: 'Próximo',
        prevBtnText: 'Voltar',
        doneBtnText: 'Entendi',
        showProgress: passos.length > 1,
        progressText: '{{current}} de {{total}}',
        steps: passos,
        onDestroyed: () => setAlvoPendente(null),
      });
      // Clicar no próprio botão destacado (ou no atalho) já é seguir o passo: fecha o balão
      [elemento, atalho].forEach((el) => el?.addEventListener('click', () => tour.destroy(), { once: true }));
      tour.drive();
    };
    procurar();
    return () => {
      cancelado = true;
    };
  }, [alvoPendente, setAlvoPendente, location.pathname]);

  const reabrir = useCallback(async () => {
    const resposta = await primeirosPassosService.reabrir();
    if (!resposta.success) {
      toast.error(resposta.error || 'Não foi possível reabrir o tour');
      return;
    }
    await carregar();
    setAbertoLocal(true);
  }, [carregar]);

  const valor: PrimeirosPassosContexto = {
    ativo,
    podeReabrir: ehAdmin && !!status?.concluido_em,
    passos,
    feitos,
    aberto,
    setAberto,
    irPara,
    irParaPlanilha: planilhaUtil ? irParaPlanilha : null,
    concluir,
    reabrir,
  };

  return (
    <Contexto.Provider value={valor}>
      {children}
      {ativo && (
        <BoasVindas
          aberta={boasVindasAberta}
          onFechar={() => setBoasVindasAberta(false)}
          nome={user?.nome ?? ''}
          empresa={empresaAtiva?.nome ?? 'sua empresa'}
          passos={passos}
          feitos={feitos}
          onComecar={(passo) => irPara(passo)}
          onPlanilha={planilhaUtil ? irParaPlanilha : null}
          onVerChecklist={() => {
            setBoasVindasAberta(false);
            setAberto(true);
          }}
        />
      )}
    </Contexto.Provider>
  );
}

interface BoasVindasProps {
  aberta: boolean;
  onFechar: () => void;
  nome: string;
  empresa: string;
  passos: PassoComEstado[];
  feitos: number;
  onComecar: (passo: Passo) => void;
  /** Atalho do cadastro inteligente (`null` quando os passos que ele cobre já estão feitos). */
  onPlanilha: (() => void) | null;
  onVerChecklist: () => void;
}

/** Chamada do cadastro inteligente: nas boas-vindas e no checklist, enquanto os 3 primeiros passos não estão feitos. */
function AtalhoPlanilha({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-md border border-primary/40 bg-primary/5 p-3 text-left transition-colors hover:bg-primary/10"
    >
      <FileSpreadsheet className="h-5 w-5 shrink-0 text-primary" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">Tem a equipe numa planilha?</span>
        <span className="block text-xs text-muted-foreground">
          Cadastre pessoas, departamentos e cargos de uma vez e conclua os 3 primeiros passos.
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

/** Janela do primeiro acesso: explica o que vem pela frente e já leva ao primeiro passo pendente. */
function BoasVindas({ aberta, onFechar, nome, empresa, passos, feitos, onComecar, onPlanilha, onVerChecklist }: BoasVindasProps) {
  const primeiroNome = nome.trim().split(/\s+/)[0] || '';
  const proximo = passos.find((p) => !p.feito);
  const botaoPrincipal = useRef<HTMLButtonElement>(null);
  return (
    <Dialog open={aberta} onOpenChange={(abrir) => !abrir && onFechar()}>
      <DialogContent
        className="max-w-md"
        onOpenAutoFocus={(e) => {
          // foco no "Começar", não no primeiro botão da janela
          e.preventDefault();
          botaoPrincipal.current?.focus();
        }}
      >
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
            <Rocket className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle>Boas-vindas ao Kora{primeiroNome ? `, ${primeiroNome}` : ''}!</DialogTitle>
          <DialogDescription>
            Vamos deixar a <span className="font-medium text-foreground">{empresa}</span> pronta para a equipe.
            {feitos > 0
              ? ` Você já fez ${feitos} de ${passos.length} passos. Faltam poucos.`
              : ` São ${passos.length} passos rápidos, uns 10 minutos no total.`}
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-2">
          {passos.map((passo, i) => {
            const Icone = passo.icone;
            return (
              <li key={passo.chave} className="flex items-center gap-3 rounded-md border border-border/60 px-3 py-2">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    passo.feito ? 'bg-green-500/15 text-green-500' : 'bg-primary/10 text-primary'
                  }`}
                >
                  {passo.feito ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </span>
                <Icone className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className={`text-sm ${passo.feito ? 'text-muted-foreground line-through' : ''}`}>{passo.titulo}</span>
              </li>
            );
          })}
        </ol>
        {onPlanilha && <AtalhoPlanilha onClick={onPlanilha} />}
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" className="text-muted-foreground" onClick={onVerChecklist}>
            Ver o checklist
          </Button>
          {proximo && (
            <Button ref={botaoPrincipal} onClick={() => onComecar(proximo)}>
              {feitos > 0 ? 'Continuar' : 'Começar'}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Botão do cabeçalho com o checklist. Só aparece pro admin enquanto o tour não foi concluído. */
export function PrimeirosPassosBotao() {
  const ctx = usePrimeirosPassos();
  if (!ctx?.ativo) return null;
  const { passos, feitos, aberto, setAberto, irPara, irParaPlanilha, concluir } = ctx;
  const total = passos.length;
  const tudoFeito = feitos === total;
  const proximo = passos.find((p) => !p.feito);

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="mr-2 h-[34px] gap-1.5 text-sm">
          <Rocket className="h-3.5 w-3.5 text-primary" />
          <span className="hidden sm:inline">Primeiros passos</span>
          <span className="rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary">
            {feitos}/{total}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(23rem,calc(100vw-1.5rem))] p-0">
        <div className="space-y-2 border-b border-border p-4">
          <div>
            <h4 className="font-semibold">{tudoFeito ? 'Tudo pronto!' : 'Primeiros passos'}</h4>
            <p className="text-xs text-muted-foreground">
              {tudoFeito
                ? 'Sua empresa está configurada. Pode encerrar o tour.'
                : 'Configure sua empresa no Kora seguindo estes passos.'}
            </p>
          </div>
          <Progress value={total ? (feitos / total) * 100 : 0} className="h-1.5" />
          {irParaPlanilha && <AtalhoPlanilha onClick={irParaPlanilha} />}
        </div>
        <ul className="max-h-[60vh] overflow-y-auto p-2">
          {passos.map((passo) => {
            const Icone = passo.icone;
            const eProximo = passo.chave === proximo?.chave;
            return (
              <li key={passo.chave}>
                <button
                  type="button"
                  onClick={() => irPara(passo)}
                  className={`flex w-full items-start gap-3 rounded-md p-2 text-left transition-colors hover:bg-muted/60 ${
                    eProximo ? 'bg-primary/5 ring-1 ring-primary/30' : ''
                  }`}
                >
                  {passo.feito ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                  ) : (
                    <Circle className={`mt-0.5 h-4 w-4 shrink-0 ${eProximo ? 'text-primary' : 'text-muted-foreground'}`} />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className={`flex items-center gap-1.5 text-sm font-medium ${passo.feito ? 'text-muted-foreground line-through' : ''}`}>
                      <Icone className="h-3.5 w-3.5 shrink-0" />
                      {passo.titulo}
                      {eProximo && (
                        <span className="ml-auto rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold uppercase text-primary">
                          Próximo
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{passo.descricao}</p>
                    {!passo.feito && passo.faltaAntes.length > 0 && (
                      <p className="mt-1 flex items-start gap-1 text-xs text-amber-500">
                        <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                        Melhor fazer antes: {passo.faltaAntes.map((p) => p.titulo.toLowerCase()).join(' e ')}.
                      </p>
                    )}
                    {!passo.feito && passo.faltaAntes.length === 0 && passo.dica && (
                      <p className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
                        <Lightbulb className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                        {passo.dica}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-end border-t border-border p-2">
          {tudoFeito ? (
            <Button size="sm" onClick={concluir}>
              Concluir
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={concluir}>
              Pular tour
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Item do menu do perfil (dentro do Popover dele) pra rever o tour depois de concluído/pulado. */
export function PrimeirosPassosMenuItem() {
  const ctx = usePrimeirosPassos();
  if (!ctx?.podeReabrir) return null;
  return (
    <PopoverClose asChild>
      <Button variant="ghost" className="w-full justify-start text-sm" onClick={ctx.reabrir}>
        <Rocket className="mr-2 h-4 w-4" />
        Primeiros passos
      </Button>
    </PopoverClose>
  );
}
